import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { DragIndicator, MoreHoriz } from '@material-ui/icons';
import CreateIcon from '@material-ui/icons/Create';
import TablePaginationActions from '../../../../../shared/table-pagination/TablePaginationActions';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import {
  CATEGORY_CREATE,
  CATEGORY_EDIT,
  CATEGORY_VIEW,
} from '../../../../../constants/AccomodationConst';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_HOTEL_ACCOMMODATIONS,
} from '../../../../../constants/RoleConsts';
import { useSelector, useDispatch } from 'react-redux';
import {
  allCategoriesSelector,
  allRoomsSelector,
  allRoomTypesSelector,
  fetchAllAccommodations,
  setAllCategories,
} from '../../../../property/propertySlice';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { deleteAccommodationCategoryAPI } from '../../../../../utils/ServicesAPIUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import VisibilityIcon from '@mui/icons-material/Visibility';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { handleOnDragEnd } from '../../../../../shared/custom-dragdrop/dragDrop';
import SearchBar from '../../../../../shared/search-bar/Search-Bar';
import { createDataSortMapping } from '../../../../../utils/PropertyAPIUtils';
import { isDragDisabled } from '../../../../../utils/formUtils';
import { decryptLocalStorage } from '../../../../../utils/authUtils';

const CategoryList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    selectedHotel,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;
  const dispatch = useDispatch();
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
  });
  const originalCategories = useSelector(allCategoriesSelector);
  const originalRoomTypes = useSelector(allRoomTypesSelector);
  const originalRooms = useSelector(allRoomsSelector);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const wrapperRef = useRef(null);
  const prevHotelIdRef = useRef(selectedHotel);
  const [popUp, setPopUp] = useState(false);
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    // assigning data set to local list while keeping an orginal data set for filtering
    setSearchText('');
    setCategories(originalCategories);
    setLoading(false);
  }, [originalCategories]);

  useEffect(() => {
    if (loading || categories?.length === 0) return;
    const totalItems = categories?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [categories?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel]);

  const handleSearchText = (searchText) => {
    setSearchText(searchText);
    const filteredRows = originalCategories.filter((item) => {
      return item.name.toLowerCase().includes(searchText.toLowerCase());
    });
    onsetPage(0);
    setCategories(filteredRows);
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onOpenDrawer = (event, type) => {
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue.category);
    handleClose();
  };

  const dragDisabled = isDragDisabled(categories, searchText);

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      category: null,
    });
  };

  const handleCellMenuClick = (event, category) => {
    event.stopPropagation();
    setSelectedValue({
      id: category.id,
      name: category.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      category: category,
    });
  };

  const deleteAccommodationCategory = async (e) => {
    e.preventDefault();
    setSelectedValue((prev) => ({
      ...prev,
      title: 'Are you sure you want to delete',
      description:
        'This action cannot be reversed. Information associated with this category will also be lost.',
      status: true,
      anchorEl: null,
    }));
  };

  const deleteCategory = async () => {
    try {
      if (selectedHotel && selectedValue && selectedValue.id) {
        const lang = decryptLocalStorage('lang');
        const delByLang = lang !== 'en' ? lang : '';
        const res = await deleteAccommodationCategoryAPI(
          selectedHotel,
          selectedValue.id,
          delByLang,
        );
        if (res.data.deleteCategory.status === 'Success') {
          dispatch(fetchAllAccommodations(selectedHotel));
          notificationManager('success', 'Category deleted successfully');
        } else {
          notificationManager('danger', 'Failed to delete Category');
        }
      }
      handleClose();
    } catch (e) {
      if (e.graphQLErrors) {
        notificationManager('danger', e.graphQLErrors[0].message);
      } else {
        notificationManager('danger', 'Failed to delete Category');
      }
      handleClose();
    }
  };

  const onDeleteCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
    });
  };

  const onViewDetails = (category, e) => {
    if (e?.target?.tagName === 'TD') {
      onViewToggle(CATEGORY_VIEW, true, category);
    }
  };

  const onClickView = (e) => {
    if (selectedValue && selectedValue.category) {
      onViewToggle(CATEGORY_VIEW, true, selectedValue.category);
    }
  };

  const retrieveNoOfRooms = (category) => {
    return originalRooms.filter((room) => room.categoryId === category.id).length;
  };

  const retrieveNoOfRoomTypes = (category) => {
    return originalRoomTypes.filter((roomType) => roomType.categoryId === category.id).length;
  };

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {selectedValue.id && selectedValue.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteCategory}
          onDeleteCancel={onDeleteCancel}
        />
      )}

      <SearchBar
        type={CATEGORY_CREATE}
        buttonName='Add Category'
        displayAddOrEdit={
          permissions[RBAC_TYPE_HOTEL_ACCOMMODATIONS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS
            ? 'show'
            : 'hide'
        }
        onOpenDrawer={onOpenDrawer}
        setSearchText={handleSearchText}
        visible={visible}
        setVisible={setVisible}
        popUp={popUp}
        setPopUp={setPopUp}
        placeHolder={categories}
        setPlaceHolder={setCategories}
        globalPlaceHolder={originalCategories}
        setGlobalPlaceHolder={setAllCategories}
        dispatch={dispatch}
        mappingApi={createDataSortMapping}
        displayText='Accommodations category'
        module='ACCOMMODATION_CATEGORY'
        searchText={searchText}
        setSearch={setSearchText}
        actionMenuItems={[]}
      />

      <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
        {loading ? (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        ) : (
          categories && (
            <TableContainer>
              <Table className='list-table' aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell className='w-40'>Category Name</TableCell>
                    <TableCell className='w-30'>No.of Room types</TableCell>
                    <TableCell className='w-20'>No.of Rooms</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>

                <DragDropContext
                  onDragEnd={(event) =>
                    handleOnDragEnd(
                      event,
                      setVisible,
                      categories,
                      setCategories,
                      false,
                      page,
                      rowsPerPage,
                    )
                  }
                  isDropDisabled={dragDisabled}
                >
                  <Droppable droppableId='spas' isDropDisabled={dragDisabled}>
                    {(provided) => (
                      <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                        {(rowsPerPage > 0
                          ? categories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          : categories
                        ).map((category, i) => (
                          <Draggable
                            key={category.id}
                            draggableId={category.id}
                            index={i}
                            isDragDisabled={dragDisabled}
                          >
                            {(provided) => (
                              <TableRow
                                key={i}
                                className='border-white pointer'
                                onClick={(e) => {
                                  onViewDetails(category, e);
                                }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <TableCell className='w-10'>
                                  {!dragDisabled && (
                                    <DragIndicator className='res-menu-accordion-drag' />
                                  )}
                                </TableCell>
                                <TableCell className='w-40'>{category?.name}</TableCell>
                                <TableCell className='w-30'>
                                  {retrieveNoOfRoomTypes(category)}
                                </TableCell>
                                <TableCell className='w-20'>
                                  {retrieveNoOfRooms(category)}
                                </TableCell>
                                <TableCell align='right'>
                                  {permissions[RBAC_TYPE_HOTEL_ACCOMMODATIONS] ===
                                    RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                                    <Button
                                      aria-controls='simple-menu'
                                      aria-haspopup='true'
                                      onClick={(e) => {
                                        handleCellMenuClick(e, category);
                                      }}
                                      className='showpopupInner'
                                    >
                                      <MoreHoriz className='showpopupInner' />
                                    </Button>
                                  )}
                                  <Menu
                                    id={i}
                                    anchorEl={selectedValue?.anchorEl}
                                    disableRestoreFocus
                                    MenuListProps={{ autoFocusItem: false }}
                                    keepMounted
                                    open={
                                      Boolean(selectedValue.anchorEl) &&
                                      selectedValue.id === category.id
                                    }
                                    onClose={handleClose}
                                    className='shadow'
                                  >
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onOpenDrawer(e, CATEGORY_EDIT);
                                      }}
                                    >
                                      <CreateIcon className='me-2 action-menu-icon' />
                                      Edit
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onClickView(e);
                                      }}
                                    >
                                      <VisibilityIcon className='me-2 action-menu-icon' />
                                      View
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        deleteAccommodationCategory(e);
                                      }}
                                    >
                                      <DeleteForeverIcon className='me-2 action-menu-icon' />
                                      Delete
                                    </MenuItem>
                                  </Menu>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>

                <TableFooter>
                  <TableRow className='paginator'>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      count={categories.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )
        )}
      </div>
    </div>
  );
};

export default CategoryList;
